<template>
    <app-tooltip :value="tooltip" :disabled="!tooltip" :color="tooltipColour" :class="classList" max-width="30rem">
        <v-btn
            ref="button"
            v-test-id="testId"
            :text="secondary"
            :color="primary ? 'primaryBackground' : color"
            :href="cleanHref"
            :depressed="depressed"
            :outlined="outlined"
            :block="block"
            :loading="loading"
            :disabled="disabled"
            :dark="!disabled"
            :small="small"
            :x-small="xSmall"
            :large="large"
            :x-large="xLarge"
            :icon="isIconButton"
            class="app-button__button"
            :tabindex="tabindex"
            :to="cleanTo"
            :target="target"
            @click="onClick"
        >
            <template v-if="findSlot('default')">
                <slot />
            </template>
            <template v-else>
                <v-icon v-if="iconLeft" :size="iconSizeValue" :color="iconColor" left>
                    {{ getIcon(iconLeft, iconPrefix) }}
                </v-icon>
                {{ text }}
                <v-icon v-if="icon" :size="iconSizeValue" :color="iconColor" :right="!isIconButton">
                    {{ getIcon(icon, iconPrefix) }}
                </v-icon>
            </template>

            <template v-if="isIconButton" #loader>
                <v-progress-circular
                    indeterminate
                    :size="small ? 14 : xSmall ? 12 : large ? 18 : xLarge ? 20 : 16"
                    :width="2"
                    :color="primary ? 'primaryBackground' : color"
                />
            </template>
        </v-btn>

        <v-menu v-if="!disabled" v-model="dropdownActive" bottom left offset-y :nudge-bottom="8">
            <template #activator="{ on, attrs }">
                <v-btn
                    v-if="dropdown"
                    v-test-id="`${testId}-dropdown`"
                    class="app-button__dropdown"
                    :text="secondary"
                    :color="primary ? 'primaryBackground' : color"
                    :depressed="depressed"
                    :outlined="outlined"
                    :disabled="disabled || loading"
                    :dark="!disabled"
                    v-bind="attrs"
                    v-on="on"
                    @click="onClickDropdown"
                >
                    <v-icon :size="iconSizeValue" :class="dropdownActive ? 'fa-rotate-180' : false">
                        {{ getIcon('subgroup') }}
                    </v-icon>
                </v-btn>
            </template>

            <slot name="dropdown" />
        </v-menu>
    </app-tooltip>
</template>

<script>
export default {
    name: 'AppButton',
    props: {
        primary: {
            type: Boolean,
            default: false
        },
        secondary: {
            type: Boolean,
            default: false
        },
        color: {
            type: String,
            default: 'primaryText'
        },
        text: {
            type: String,
            default: null
        },
        tooltip: {
            type: [String, Array],
            default: null
        },
        tooltipColour: {
            type: String,
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        depressed: {
            type: Boolean,
            default: false
        },
        small: {
            type: Boolean,
            default: false
        },
        xSmall: {
            type: Boolean,
            default: false
        },
        large: {
            type: Boolean,
            default: false
        },
        xLarge: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        block: {
            type: Boolean,
            default: false
        },
        icon: {
            type: [String, Boolean],
            default: null
        },
        iconPrefix: {
            type: String,
            default: null
        },
        iconLeft: {
            type: String,
            default: null
        },
        iconSize: {
            type: String,
            default: null
        },
        iconColor: {
            type: String,
            default: null
        },
        to: {
            type: [String, Object],
            default: null
        },
        href: {
            type: String,
            default: null
        },
        target: {
            type: String,
            default: null
        },
        testId: {
            type: String,
            default: null
        },
        tabindex: {
            type: [String, Number],
            default: null
        },
        dropdown: {
            type: Boolean,
            default: false
        }
    },
    data: () => ({
        dropdownActive: false
    }),
    computed: {
        cleanHref() {
            if (!this._.isString(this.href)) {
                return this.href;
            }

            let url = this.href;

            if (url.includes('http://')) {
                url = url.replace('http://', '//');
            }

            if (url.includes('https://')) {
                url = url.replace('https://', '//');
            }

            return url;
        },
        isIconButton() {
            return this.icon && !this.text;
        },
        iconSizeValue() {
            if (this.iconSize) {
                return this.iconSize;
            }
            if (this.xLarge) {
                return 'x-large';
            }
            if (this.large) {
                return '2em';
            }
            if (this.small) {
                return 'small';
            }
            return '1.1rem';
        },
        classList() {
            let classList = ['app-button'];

            if (this.disabled) {
                classList.push('app-button--disabled');
            }

            if (this.dropdown && !this.disabled) {
                classList.push('app-button--dropdown');
            }

            if (this.secondary) {
                classList.push('app-button--secondary');
            }

            return classList.join(' ');
        },
        cleanTo() {
            if (typeof this.to !== 'string') {
                return this.to;
            }

            let url = this.to;

            if (url.includes('http://')) {
                url = url.replace('http://', '//');
            }

            if (url.includes('https://')) {
                url = url.replace('https://', '//');
            }

            return url;
        }
    },
    methods: {
        onClick($event) {
            if (this.disabled) {
                return;
            }

            this.$emit('click', $event);
        },
        onClickDropdown($event) {
            if (this.disabled) {
                return;
            }
            this.$emit('click:dropdown', $event);

            this.dropdownActive = !this.dropdownActive;
        }
    }
};
</script>

<style lang="scss">
.app-button {
    &--disabled {
        .v-image {
            opacity: 0.5;
            filter: grayscale(100%);
        }
    }

    &--dropdown {
        display: inline-flex;
        flex-direction: row;
        align-items: stretch;
        .app-button__button {
            border-radius: 4px 0 0 4px;
        }
    }

    &--secondary {
        .v-btn::before {
            opacity: 0.08;
        }
        .v-btn:hover::before {
            opacity: 0.16;
        }
    }

    &__dropdown {
        min-width: 0.5rem !important;
        border-radius: 0 4px 4px 0;
        margin-left: 1px;
    }
}
</style>
